import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/satExam.css';
import '../styles/practiceQuizzes.css';
import { useCurrentUser } from '../contexts/UserContext';
import { submitAttempts } from '../services/questionService';

function qId(question) {
  return String(question._id || question.id);
}

function getOptions(question) {
  if (question.format === 'truefalse') {
    return [
      { id: 'TRUE', bubble: 'T', text: 'True' },
      { id: 'FALSE', bubble: 'F', text: 'False' },
    ];
  }
  return (question.options || []).map((o) => ({ ...o, bubble: o.id }));
}

function isOptionCorrect(question, optionId) {
  if (question.format === 'truefalse') {
    const expected = question.answer?.value;
    return (optionId === 'TRUE' && expected === true) || (optionId === 'FALSE' && expected === false);
  }
  return question.options?.find((o) => o.id === optionId)?.correct ?? false;
}

function isCorrect(question, answers) {
  const selected = answers[qId(question)];
  if (selected === undefined) return false;
  return isOptionCorrect(question, selected);
}

function getRationale(question) {
  const correctOption = question.options?.find((o) => o.correct);
  return correctOption?.rationale || question.answer?.explanation || null;
}

// ── Session screen ────────────────────────────────────────────────────────────

function SessionScreen({
  questions, currentIndex, answers, courseLabel, moduleHeading,
  onAnswer, onNavigate, onSubmit,
}) {
  const question = questions[currentIndex];
  if (!question) return null;

  const id = qId(question);
  const selectedAnswer = answers[id];
  const answeredCount = questions.filter((q) => answers[qId(q)] !== undefined).length;
  const options = getOptions(question);
  const hasOptions = options.length > 0;

  return (
    <div className="sat-shell sat-shell--exam">
      <header className="sat-header">
        <div className="sat-header-left">
          <span className="sat-badge sat-badge--module">{courseLabel}</span>
        </div>
        <div className="sat-header-right">
          <span className="sat-badge sat-badge--section">{moduleHeading}</span>
        </div>
      </header>

      <main className="sat-main">
        <div className="sat-question-card">
          <p className="sat-question-counter">
            Question {currentIndex + 1} <span className="sat-question-of">of {questions.length}</span>
          </p>

          {question.paragraph && (
            <div className="sat-paragraph-box">
              <p className="sat-paragraph">{question.paragraph}</p>
            </div>
          )}

          <p className="sat-stem">{question.stem}</p>

          {hasOptions ? (
            <div className="sat-options">
              {options.map((option) => (
                <button
                  key={option.id}
                  className={`sat-option${selectedAnswer === option.id ? ' sat-option--selected' : ''}`}
                  onClick={() => onAnswer(id, option.id)}
                >
                  <span className="sat-option-bubble">{option.bubble}</span>
                  <span className="sat-option-text">{option.text}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="practice-open-response">Open response — jot notes in your learning journal.</p>
          )}
        </div>
      </main>

      <footer className="sat-footer">
        <div className="sat-footer-left">
          <span className="sat-answered-count">{answeredCount} of {questions.length} answered</span>
        </div>

        <div className="sat-footer-bubbles">
          <div className="sat-nav-grid">
            {questions.map((q, i) => {
              const answered = answers[qId(q)] !== undefined;
              const current = i === currentIndex;
              return (
                <button
                  key={qId(q)}
                  className={`sat-nav-dot${answered ? ' answered' : ''}${current ? ' current' : ''}`}
                  onClick={() => onNavigate(i)}
                  aria-label={`Question ${i + 1}${answered ? ', answered' : ''}`}
                  aria-current={current ? 'true' : undefined}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>

        <div className="sat-footer-right">
          <button
            className="sat-btn sat-btn--secondary"
            onClick={() => onNavigate(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            Back
          </button>
          <button
            className="sat-btn sat-btn--secondary"
            onClick={() => onNavigate(Math.min(questions.length - 1, currentIndex + 1))}
            disabled={currentIndex === questions.length - 1}
          >
            Next
          </button>
          <button className="sat-btn sat-btn--primary" onClick={onSubmit}>
            Submit
          </button>
        </div>
      </footer>
    </div>
  );
}

// ── Results screen ────────────────────────────────────────────────────────────

function ResultsScreen({ questions, answers, courseLabel, moduleHeading, onBack }) {
  const correctCount = questions.filter((q) => isCorrect(q, answers)).length;
  const skippedCount = questions.filter((q) => answers[qId(q)] === undefined).length;
  const incorrectCount = questions.length - correctCount - skippedCount;
  const pct = questions.length ? Math.round((correctCount / questions.length) * 100) : 0;

  const byDifficulty = useMemo(() => {
    const map = {};
    questions.forEach((q) => {
      const lvl = q.difficulty;
      if (!Number.isFinite(lvl)) return;
      if (!map[lvl]) map[lvl] = { total: 0, correct: 0 };
      map[lvl].total++;
      if (isCorrect(q, answers)) map[lvl].correct++;
    });
    return Object.entries(map)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([lvl, stat]) => ({ level: Number(lvl), ...stat }));
  }, [questions, answers]);

  return (
    <div className="sat-shell sat-shell--results">
      <header className="sat-header">
        <div className="sat-header-left">
          <span className="sat-badge sat-badge--module">{courseLabel}</span>
        </div>
        <div className="sat-header-right">
          <span className="sat-badge sat-badge--section">{moduleHeading}</span>
        </div>
      </header>

      <div className="sat-results-body">
        <div className="sat-results-titlebar">
          <p className="sat-eyebrow">Practice Results</p>
          <button className="sat-btn sat-btn--secondary" onClick={onBack}>
            Back to Practice
          </button>
        </div>

        <div className="sat-results-stats">
          <div className="sat-results-stat sat-results-stat--score">
            <span className="sat-results-stat-value">{pct}%</span>
            <span className="sat-results-stat-label">Score</span>
          </div>
          <div className="sat-results-stat-divider" />
          <div className="sat-results-stat">
            <span className="sat-results-stat-value sat-results-stat-value--correct">{correctCount}</span>
            <span className="sat-results-stat-label">Correct</span>
          </div>
          <div className="sat-results-stat-divider" />
          <div className="sat-results-stat">
            <span className="sat-results-stat-value sat-results-stat-value--incorrect">{incorrectCount}</span>
            <span className="sat-results-stat-label">Incorrect</span>
          </div>
          {skippedCount > 0 && (
            <>
              <div className="sat-results-stat-divider" />
              <div className="sat-results-stat">
                <span className="sat-results-stat-value">{skippedCount}</span>
                <span className="sat-results-stat-label">Skipped</span>
              </div>
            </>
          )}
          {byDifficulty.length > 1 && (
            <>
              <div className="sat-results-stat-divider" />
              <div className="sat-results-stat">
                <div className="sat-results-stat-difficulty">
                  {byDifficulty.map((b) => (
                    <span key={b.level} className="sat-results-stat-label">
                      Lvl {b.level}: {b.correct}/{b.total}
                    </span>
                  ))}
                </div>
                <span className="sat-results-stat-label">By Level</span>
              </div>
            </>
          )}
        </div>

        <div className="sat-review">
          <div className="sat-review-list">
          {questions.map((q, i) => {
            const id = qId(q);
            const userAnswer = answers[id];
            const correct = isCorrect(q, answers);
            const options = getOptions(q);
            const rationale = getRationale(q);

            return (
              <div
                key={id}
                className={`sat-review-q${correct ? ' sat-review-q--correct' : ' sat-review-q--incorrect'}`}
              >
                <div className="sat-review-q-header">
                  <span className="sat-review-q-num">Q{i + 1}</span>
                  <span className={`sat-review-verdict${correct ? ' correct' : ' incorrect'}`}>
                    {correct ? 'Correct' : 'Incorrect'}
                  </span>
                  {!userAnswer && <span className="sat-review-skipped">Not answered</span>}
                  {Number.isFinite(q.difficulty) && (
                    <span className="sat-review-skipped">Lvl {q.difficulty}</span>
                  )}
                </div>

                {q.paragraph && (
                  <p className="sat-review-paragraph">{q.paragraph}</p>
                )}
                <p className="sat-review-stem">{q.stem}</p>

                {options.length > 0 && (
                  <div className="sat-review-options">
                    {options.map((opt) => {
                      const isUserChoice = opt.id === userAnswer;
                      const isCorrectOpt = isOptionCorrect(q, opt.id);
                      return (
                        <div
                          key={opt.id}
                          className={`sat-review-option${isCorrectOpt ? ' is-correct' : ''}${isUserChoice && !isCorrectOpt ? ' is-wrong' : ''}`}
                        >
                          <span className="sat-option-bubble">{opt.bubble}</span>
                          <span className="sat-option-text">{opt.text}</span>
                          <div className="sat-review-tags">
                            {isUserChoice && (
                              <span className="sat-review-tag sat-review-tag--user">Your answer</span>
                            )}
                            {isCorrectOpt && (
                              <span className="sat-review-tag sat-review-tag--correct">Correct</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {rationale && (
                  <p className="sat-review-rationale">{rationale}</p>
                )}
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function PracticeSession() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const quizData = location.state?.quiz;

  const isReplay = !!location.state?.replay;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState(isReplay ? (location.state?.replayAnswers || {}) : {});
  const [phase, setPhase] = useState(isReplay ? 'results' : 'session');

  const questions = useMemo(
    () => (Array.isArray(quizData?.questions) ? quizData.questions : []),
    [quizData],
  );
  const courseLabel = quizData?.courseLabel || 'Course';
  const moduleLabels = Array.isArray(quizData?.moduleLabels) ? quizData.moduleLabels : [];

  const moduleHeading = useMemo(() => {
    if (moduleLabels.length === 1) return moduleLabels[0].title;
    if (moduleLabels.length > 1) return `${moduleLabels.length} modules`;
    return 'Practice';
  }, [moduleLabels]);

  const handleAnswer = useCallback((questionId, optionId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  }, []);

  const handleNavigate = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const handleSubmit = useCallback(() => {
    setPhase('results');

    const userId = currentUser?.id || currentUser?._id;
    if (userId) {
      const attemptAnswers = questions
        .filter((q) => answers[qId(q)] !== undefined)
        .map((q) => ({
          questionId: qId(q),
          courseId: String(q.courseId || ''),
          moduleId: String(q.moduleId || ''),
          submodule: q.submodule || undefined,
          difficulty: Number.isFinite(q.difficulty) ? q.difficulty : undefined,
          correct: isCorrect(q, answers),
          selectedAnswer: answers[qId(q)],
        }));

      if (attemptAnswers.length) {
        submitAttempts({
          userId: String(userId),
          sessionId: quizData?.generatedAt,
          answers: attemptAnswers,
        }).catch((err) => console.warn('[PracticeSession] submitAttempts failed:', err.message));
      }
    }
  }, [currentUser, questions, answers, quizData]);

  const handleBack = useCallback(() => {
    navigate('/practice');
  }, [navigate]);

  if (!quizData || !questions.length) {
    return (
      <div className="sat-shell sat-shell--centered">
        <div className="sat-intro-card">
          <p className="sat-eyebrow">Practice set</p>
          <h2 className="sat-intro-title">No quiz ready</h2>
          <p className="sat-intro-sub">Design your mix first to load a quiz.</p>
          <button className="sat-btn sat-btn--primary sat-btn--lg" onClick={() => navigate('/practice')}>
            Design your quiz mix
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'results') {
    return (
      <ResultsScreen
        questions={questions}
        answers={answers}
        courseLabel={courseLabel}
        moduleHeading={moduleHeading}
        onBack={handleBack}
      />
    );
  }

  return (
    <SessionScreen
      questions={questions}
      currentIndex={currentIndex}
      answers={answers}
      courseLabel={courseLabel}
      moduleHeading={moduleHeading}
      onAnswer={handleAnswer}
      onNavigate={handleNavigate}
      onSubmit={handleSubmit}
    />
  );
}
